import React, { useCallback } from 'react';
import type { NativeSyntheticEvent } from 'react-native';
import {
  Box,
  HStack,
  Pressable,
  Text,
} from '@gluestack-ui/themed';

const SWITCH_ON_TRACK = '#344054';

export type SwitchFilterProps = {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  testID: string;
};

type SwitchKeyDownEvent = NativeSyntheticEvent<{ key: string }> & {
  key?: string;
  preventDefault?: () => void;
};

/**
 * Boolean metrics filter: one focusable switch control with painted track and knob.
 */
export function SwitchFilter({
  label,
  value,
  onChange,
  disabled = false,
  testID,
}: SwitchFilterProps) {
  const toggle = useCallback(() => {
    if (!disabled) {
      onChange(!value);
    }
  }, [disabled, onChange, value]);

  const handleKeyDown = useCallback(
    (event: SwitchKeyDownEvent) => {
      const key = event.nativeEvent?.key ?? event.key;
      if (key === ' ' || key === 'Spacebar' || key === 'Space') {
        event.preventDefault?.();
        toggle();
      }
    },
    [toggle],
  );

  return (
    <Pressable
      accessible
      testID={`${testID}-control`}
      disabled={disabled}
      onPress={toggle}
      // RN Web still delivers onKeyDown; Gluestack's Pressable types omit it.
      {...({ onKeyDown: handleKeyDown } as unknown as React.ComponentProps<typeof Pressable>)}
      accessibilityRole="switch"
      accessibilityState={{ checked: value, disabled: !!disabled }}
      accessibilityLabel={label}
      aria-checked={value}
      aria-label={label}
      minHeight={32}
      justifyContent="center"
      py="$1"
    >
      <HStack
        alignItems="center"
        space="sm"
        pointerEvents="none"
        importantForAccessibility="no"
      >
        <Box
          testID={`${testID}-track`}
          width={32}
          height={18}
          borderRadius="$full"
          bg={value ? undefined : '$bgSubtle'}
          borderWidth={value ? 0 : 1}
          borderColor="$borderLight"
          justifyContent="center"
          px="$0.5"
          style={value ? { backgroundColor: SWITCH_ON_TRACK } : undefined}
        >
          <Box
            testID={`${testID}-knob`}
            width={14}
            height={14}
            borderRadius="$full"
            bg="$bgCard"
            alignSelf={value ? 'flex-end' : 'flex-start'}
          />
        </Box>
        <Text size="sm" color="$textBody" accessible={false} importantForAccessibility="no">
          {label}
        </Text>
      </HStack>
    </Pressable>
  );
}
